import {
  FormSubmitButton,
  FormProvider,
  FormTimezone,
  Timezones,
  useForm,
} from '@mfw/forms';
import { config as translationsConfig } from '@mfw/translations';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIntl } from '@mfw/components';
import Box from '@mui/material/Box';
import React from 'react';

import type { ClientOnboardingStepsType } from '../../clientOnboarding.steps';
import type { UiUserTimezoneStep } from '../../../../../schemas/users.schema';

import { UiUserTimezoneStepSchema } from '../../../../../schemas/users.schema';
import { useUpdateUser } from '../../../../../hooks/usersv2/useUpdateUser';
import { LocaleSelector } from '../../../../../components';
import OnboardingHeader from '../../../OnboardingHeader';
import { useTenantContext } from '../../../../../context/TenantProvider';
import { useAuthContext } from '../../../../../context';
import { accountLocale } from '../../../../../utils/accountLocale';
import messages from '../TimezoneLanguageStep/messages';
import { useWizard } from '../../../../../allFeatures';
import generalMessages from '../../../../../messages';
import { FormatDate } from '../../../../../utils';

export const TimezoneLanguageDialogStep = () => {
  const { formatMessage } = useIntl();
  const { goToStep } = useWizard<ClientOnboardingStepsType>();
  const { currentUser } = useAuthContext();
  const { tenant } = useTenantContext();
  const { mutateAsync: updateUserV2Async } = useUpdateUser();

  // Same order as the profile dialog: the explicit choice, then the zone the
  // browser last reported (what the server is already scheduling in), then the
  // live browser zone.
  const defaultTimezone = React.useMemo(
    () =>
      currentUser?.settings?.timezone ||
      currentUser?.settings?.detectedTimezone ||
      FormatDate.currentTimezone(),
    [currentUser?.settings?.detectedTimezone, currentUser?.settings?.timezone],
  );

  const defaultValues = async () => {
    return {
      locale: [
        {
          label: '',
          value:
            accountLocale(currentUser?.settings, tenant?.defaultLocale) ||
            translationsConfig.defaultLocale,
        },
      ],
      timezone: [
        {
          label:
            Timezones.find((timezone) => timezone.tzCode === defaultTimezone)
              ?.label ?? '',
          value: defaultTimezone,
        },
      ],
    } as UiUserTimezoneStep;
  };

  const methods = useForm({
    defaultValues,
    resolver: zodResolver(UiUserTimezoneStepSchema),
  });

  const handleNext = async (settings: UiUserTimezoneStep) => {
    await updateUserV2Async({
      userSettings: { locale: settings.locale, timezone: settings.timezone },
    });
    goToStep('basicInfo');
  };

  return (
    <FormProvider onSubmit={handleNext} {...methods}>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          position: 'sticky',
          px: 3,
          py: 2,
          top: 0,
          zIndex: 2,
        }}
      >
        <OnboardingHeader
          description={formatMessage(messages.description)}
          title={formatMessage(messages.title)}
        />
      </Box>
      <Box
        sx={{
          '& .Mfw-Autocomplete-inputLabel__wrapper': {
            backgroundColor: 'background.paper',
          },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          px: 3,
          py: 2,
        }}
      >
        <FormTimezone
          title={formatMessage(messages.timezone)}
          label={formatMessage(messages.timezone)}
          data-testid="client-onboarding-timezone"
          className="mb-4 bg-background-paper"
          name="timezone"
        />
        <LocaleSelector
          label={formatMessage(messages.language)}
          data-testid="client-onboarding-locale"
          name="locale"
          asFormField
        />
      </Box>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          bottom: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          position: 'sticky',
          px: 2,
          py: 1.5,
          zIndex: 1,
        }}
      >
        <FormSubmitButton
          data-testid="onboarding-next"
          variant="contained"
          disableIfNotDirty
          size="large"
        >
          {formatMessage(generalMessages.next)}
        </FormSubmitButton>
      </Box>
    </FormProvider>
  );
};
